import clsx from 'clsx';
import { CalendarClock, CalendarDays, Columns3, List } from 'lucide-react';
import { useT } from '../../i18n';
import styles from './ViewToggle.module.css';

export type InquiriesView = 'board' | 'list' | 'activity' | 'calendar';

export interface ViewToggleProps {
  value: InquiriesView;
  onChange: (view: InquiriesView) => void;
  /** Overdue-deals badge shown on the «По делам» tab (issue #52). */
  overdueBadge?: number;
}

/**
 * Kanban | List | «По делам» | Calendar segmented control from the Board subbar
 * (modtab pills). Exposes tablist/tab semantics — selection is visual-only
 * (panels are rendered conditionally by the page, so no aria-controls wiring).
 */
export function ViewToggle({ value, onChange, overdueBadge = 0 }: ViewToggleProps) {
  const { t } = useT();
  const options = [
    { id: 'board' as const, label: t('inquiries.viewBoard'), icon: <Columns3 size={15} aria-hidden="true" /> },
    { id: 'list' as const, label: t('inquiries.viewList'), icon: <List size={15} aria-hidden="true" /> },
    { id: 'activity' as const, label: t('inquiries.viewActivity'), icon: <CalendarClock size={15} aria-hidden="true" /> },
    { id: 'calendar' as const, label: t('inquiries.viewCalendar'), icon: <CalendarDays size={15} aria-hidden="true" /> },
  ];
  return (
    <div className={styles.seg} role="tablist" aria-label={t('inquiries.viewLabel')}>
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          role="tab"
          aria-selected={value === opt.id}
          className={clsx(styles.tab, value === opt.id && styles.on)}
          onClick={() => onChange(opt.id)}
        >
          {opt.icon}
          {opt.label}
          {opt.id === 'activity' && overdueBadge > 0 && (
            <span
              className={styles.badge}
              aria-label={t('inquiries.overdueCount', { count: overdueBadge })}
            >
              {overdueBadge}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}
